import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { ReviewsSection } from "@/components/ReviewsSection";
import { api } from "@/lib/api";
import { BookingModal } from "@/components/BookingModal";
import { shareWhatsApp } from "@/lib/whatsapp";
import { Star, Clock, Check, MessageCircle, ArrowLeft, Loader2, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";

export default function PackageDetail() {
  const { id } = useParams();
  const { t, lang, money } = useApp();
  const nav = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [booking, setBooking] = useState(null);
  const [departure, setDeparture] = useState(null);

  useEffect(() => { api.get(`/packages/${id}`).then((r) => setPkg(r.data)).catch(() => nav("/packages")); }, [id, nav]);

  const jsonLd = useMemo(() => pkg ? {
    "@context": "https://schema.org", "@type": "TouristTrip", "name": pkg[`name_${lang}`], "description": pkg[`desc_${lang}`],
    "image": pkg.image, "touristType": pkg.category, "itinerary": (pkg[`itinerary_${lang}`] || []).map((n) => ({ "@type": "TouristAttraction", "name": n })),
    "offers": { "@type": "Offer", "price": pkg.price, "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
    "provider": { "@type": "TravelAgency", "name": "Safeer Baghdad" },
  } : null, [pkg, lang]);
  useSeo({ title: pkg?.[`name_${lang}`], description: pkg?.[`desc_${lang}`], image: pkg?.image, type: "product", jsonLd });

  if (!pkg) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-tigris" size={40} /></div>;

  const share = () => {
    const msg = lang === "ar"
      ? `🌟 ${pkg.name_ar}\n${pkg.desc_ar}\n⏱ ${pkg.duration_ar}\n💰 ${money(pkg.price)}\nسفير بغداد`
      : `🌟 ${pkg.name_en}\n${pkg.desc_en}\n⏱ ${pkg.duration_en}\n💰 ${money(pkg.price)}\nSafeer Baghdad`;
    shareWhatsApp(msg);
  };

  return (
    <div className="bg-sand min-h-screen">
      <div className="relative h-[46vh] overflow-hidden">
        <img src={pkg.image} alt={pkg[`name_${lang}`]} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-tigris-dark/90 to-tigris-dark/20" />
        <div className="absolute bottom-0 inset-x-0 max-w-5xl mx-auto px-4 sm:px-6 pb-8 text-white">
          <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-1 text-sm text-white/80 mb-3 hover:text-white">
            <ArrowLeft size={16} className="rtl:rotate-180" /> {t.common.back}
          </button>
          <span className="inline-flex items-center gap-1 bg-white/20 text-xs font-bold px-3 py-1 rounded-full mb-2">
            <Star size={12} className="fill-gold text-gold" /> {pkg.rating}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-display">{pkg[`name_${lang}`]}</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <p className="text-slate-600 leading-relaxed mb-3 flex items-center gap-2 text-sm">
            <Clock size={16} className="text-tigris" /> {pkg[`duration_${lang}`]}
          </p>
          <p className="text-slate-600 leading-relaxed mb-8">{pkg[`desc_${lang}`]}</p>
          <h2 className="text-xl font-bold text-slate-800 mb-5">{t.common.itinerary}</h2>
          <div className="space-y-4">
            {pkg[`itinerary_${lang}`].map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className="w-8 h-8 rounded-full bg-tigris text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  {i < pkg[`itinerary_${lang}`].length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" />}
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-100 flex-1 shadow-sm">
                  <p className="text-sm text-slate-700 font-medium flex items-center gap-2"><Check size={15} className="text-tigris" /> {step}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <ReviewsSection type="package" itemId={pkg.id} />
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-md sticky top-24">
            <span className="text-xs text-slate-400">{t.common.from}</span>
            <div className="text-3xl font-extrabold text-tigris mb-5">{money(pkg.price)}</div>
            {pkg.departures?.length > 0 && (
              <div className="mb-5" data-testid="departures-list">
                <div className="text-xs font-bold text-slate-600 mb-2">{t.departures.title}</div>
                <div className="space-y-2">
                  {pkg.departures.map((dep) => (
                    <button key={dep.date} type="button" data-testid={`departure-${dep.date}`} onClick={() => setDeparture(dep)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 text-sm transition-colors ${departure?.date === dep.date ? "border-gold bg-gold/5" : "border-slate-100 hover:border-slate-200"}`}>
                      <span className="flex items-center gap-2 font-semibold text-slate-700"><CalendarDays size={15} className="text-tigris" /> <span dir="ltr">{dep.date}</span></span>
                      <span className="text-end"><span className="font-extrabold text-tigris">{money(dep.price)}</span><span className="block text-[10px] text-slate-400">{dep.seats} {t.departures.seats}</span></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="detail-book-btn" disabled={pkg.departures?.length > 0 && !departure}
              onClick={() => setBooking({ type: "package", item_id: pkg.id, title: `${pkg[`name_${lang}`]}${departure ? ` · ${departure.date}` : ""}`, amount: departure ? departure.price : pkg.price, details: departure ? { departure_date: departure.date } : {} })}
              className="btn-gold w-full py-3.5 rounded-xl font-bold mb-3 disabled:opacity-50 disabled:cursor-not-allowed">
              {pkg.departures?.length > 0 && !departure ? t.departures.choose : t.common.bookNow}
            </button>
            <button data-testid="detail-share-btn" onClick={share} className="btn-wa w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2">
              <MessageCircle size={18} /> {t.common.shareWhatsapp}
            </button>
          </div>
        </div>
      </div>
      {booking && <BookingModal item={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}
